import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';

interface SignatureCanvasProps {
  onSave: (blob: Blob) => void;
  onCancel: () => void;
  disabled?: boolean;
}

/** Freehand signature capture (mouse/touch) exported as a PNG blob on save. */
export function SignatureCanvas({ onSave, onCancel, disabled = false }: SignatureCanvasProps) {
  const { t } = useTranslation('customFields');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  function getContext() {
    return canvasRef.current?.getContext('2d') ?? null;
  }

  function pointerPosition(e: React.PointerEvent<HTMLCanvasElement>) {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function handlePointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    if (disabled) return;
    drawingRef.current = true;
    const ctx = getContext();
    const { x, y } = pointerPosition(e);
    ctx?.beginPath();
    ctx?.moveTo(x, y);
  }

  function handlePointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawingRef.current || disabled) return;
    const ctx = getContext();
    if (!ctx) return;
    const { x, y } = pointerPosition(e);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#000000';
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDrawn(true);
  }

  function handlePointerUp() {
    drawingRef.current = false;
  }

  function handleClear() {
    const canvas = canvasRef.current;
    const ctx = getContext();
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  }

  function handleSave() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (blob) onSave(blob);
    }, 'image/png');
  }

  return (
    <div className="flex flex-col gap-2">
      <canvas
        ref={canvasRef}
        width={400}
        height={160}
        className="h-40 w-full touch-none rounded-md border border-input bg-background"
        data-testid="custom-fields-signature-canvas"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
      />
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleClear}
          disabled={disabled || !hasDrawn}
          data-testid="custom-fields-signature-clear"
        >
          {t('fieldValue.signature.clear')}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onCancel}
          disabled={disabled}
          data-testid="custom-fields-signature-cancel"
        >
          {t('fieldValue.signature.cancel')}
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={handleSave}
          disabled={disabled || !hasDrawn}
          data-testid="custom-fields-signature-save"
        >
          {t('fieldValue.signature.save')}
        </Button>
      </div>
    </div>
  );
}
